import React, { useState, useEffect, useRef, useContext } from "react";
import "./godisnjiizvestaj.css";
import { baseRequest } from "../../../axiosconfig";
import Loading from "../../loading";
import { useReactToPrint } from "react-to-print";
import { PRINT_PAGE_STYLE } from "../../../utils/printPageStyle";
import { Link } from "react-router-dom";
import { UserContext } from "../../../userContext";
import { ROLES } from "../../../constants/roles";

const LEVEL_LABELS = { nizak: "Nizak", srednji: "Srednji", visok: "Visok", bez_ocene: "Bez ocene" };
const MEASURE_LABELS = {
  zavrseno: "Završeno",
  probijen_rok: "Probijen rok",
  istice_uskoro: "Ističe uskoro",
  u_roku: "U roku",
  bez_roka: "Bez roka",
};

function GodisnjiIzvestaj() {
  const { user } = useContext(UserContext);
  // Isto pravilo kao na serveru (requireRole(1, 2) za snimanje zaključka)
  const canEditNote =
    user &&
    (user.role_id === ROLES.SUPER_ADMIN || user.role_id === ROLES.MANAGER);

  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [noteMessage, setNoteMessage] = useState("");
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: PRINT_PAGE_STYLE,
  });

  useEffect(() => {
    setLoading(true);
    setNoteMessage("");
    baseRequest
      .get(`/annual_report/${year}`)
      .then((res) => {
        setData(res.data);
        // zaključak je vezan za godinu — učitava se iz upitnika te godine
        setNote(res.data.questionnaire?.note || "");
        setLoading(false);
      })
      .catch(() => {
        setData(null);
        setNote("");
        setLoading(false);
      });
  }, [year]);

  const handleSaveNote = () => {
    if (!data?.questionnaire?.report_id) return;
    setLoading(true);
    baseRequest
      .put(`/self_assessment_report/${data.questionnaire.report_id}/note`, { note })
      .then(() => {
        setLoading(false);
        setNoteMessage("Zaključak je sačuvan.");
      })
      .catch(() => {
        setLoading(false);
        setNoteMessage("Čuvanje zaključka nije uspelo.");
      });
  };

  const renderReport = (forPrint) => {
    if (!data) return null;
    const q = data.questionnaire;
    const s = data.stats;

    return (
      <div className={forPrint ? "godisnji-izvestaj-print" : ""}>
        <h2 className="godisnji-izvestaj-heading">
          GODIŠNJI IZVEŠTAJ O SISTEMU FINANSIJSKOG UPRAVLJANJA I KONTROLE ZA{" "}
          {data.year}. GODINU
        </h2>
        {data.organization?.name && (
          <p className="godisnji-izvestaj-org">
            Korisnik javnih sredstava: <strong>{data.organization.name}</strong>
          </p>
        )}

        <h3>1. Rezime samoprocene po oblastima</h3>
        {q ? (
          <>
            <table className="godisnji-izvestaj-table">
              <thead>
                <tr>
                  <th>OBLAST</th>
                  <th style={{ width: "70px" }}>DA</th>
                  <th style={{ width: "70px" }}>NE</th>
                  <th style={{ width: "100px" }}>DELIMIČNO</th>
                  <th style={{ width: "120px" }}>BEZ ODGOVORA</th>
                </tr>
              </thead>
              <tbody>
                {q.sections.map((sec) => (
                  <tr key={sec.code}>
                    <td>
                      {sec.order}. {sec.name}
                    </td>
                    <td>{sec.da}</td>
                    <td>{sec.ne}</td>
                    <td>{sec.delimicno}</td>
                    <td>{sec.bez_odgovora}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="godisnji-izvestaj-note-small">
              Status upitnika: {q.status === "zavrsen" ? "Završen" : "U izradi"}
              {q.finalized_at &&
                ` (završen ${new Date(q.finalized_at).toLocaleDateString("sr-RS")})`}
            </p>
          </>
        ) : (
          <p className="godisnji-izvestaj-warning">
            Upitnik za samoprocenu za {data.year}. godinu nije popunjen.{" "}
            {!forPrint && (
              <Link to="/admin/samoprocena">Popunite upitnik ovde.</Link>
            )}
          </p>
        )}

        <h3>2. Poslovni procesi i procedure</h3>
        <table className="godisnji-izvestaj-table">
          <tbody>
            <tr>
              <td>Broj organizacionih celina sa popisanim procesima</td>
              <td style={{ width: "100px" }}>{s.business_processes_count}</td>
            </tr>
            <tr>
              <td>Broj popisanih poslovnih procesa</td>
              <td>{s.process_lists_count}</td>
            </tr>
            <tr>
              <td>Broj mapa procesa (od toga odobrenih)</td>
              <td>
                {s.process_maps_count} ({s.process_maps_approved_count})
              </td>
            </tr>
            <tr>
              <td>Broj procedura</td>
              <td>{s.procedures_count}</td>
            </tr>
          </tbody>
        </table>

        <h3>3. Registar rizika</h3>
        <table className="godisnji-izvestaj-table">
          <tbody>
            <tr>
              <td>Ukupan broj identifikovanih rizika</td>
              <td style={{ width: "100px" }}>{s.risks_total}</td>
            </tr>
            {Object.entries(s.risks_by_level).map(([level, count]) => (
              <tr key={level}>
                <td>Rizici — nivo: {LEVEL_LABELS[level] || level}</td>
                <td>{count}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {Object.keys(s.risks_by_group).length > 0 && (
          <table className="godisnji-izvestaj-table">
            <thead>
              <tr>
                <th>GRUPA RIZIKA</th>
                <th style={{ width: "100px" }}>BROJ</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(s.risks_by_group).map(([group, count]) => (
                <tr key={group}>
                  <td>{group}</td>
                  <td>{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3>4. Mere za upravljanje rizicima</h3>
        <table className="godisnji-izvestaj-table">
          <tbody>
            <tr>
              <td>Ukupan broj definisanih mera</td>
              <td style={{ width: "100px" }}>{s.measures_total}</td>
            </tr>
            {Object.entries(s.measures_by_status).map(([status, count]) => (
              <tr key={status}>
                <td>Mere — status: {MEASURE_LABELS[status] || status}</td>
                <td>{count}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3>5. Zaključak i napomene</h3>
        {forPrint ? (
          <p className="godisnji-izvestaj-zakljucak">{note || "—"}</p>
        ) : (
          <>
            <textarea
              className="godisnji-izvestaj-textarea"
              placeholder={
                q
                  ? "Zaključak i napomene rukovodstva (ulazi u štampanu verziju izveštaja)..."
                  : "Za trajno čuvanje zaključka prvo kreirajte upitnik za samoprocenu za ovu godinu."
              }
              value={note}
              readOnly={!canEditNote}
              onChange={(e) => {
                setNote(e.target.value);
                setNoteMessage("");
              }}
            />
            {q && canEditNote && (
              <div className="godisnji-izvestaj-note-actions">
                <button className="add-poslovni-proces-btn" onClick={handleSaveNote}>
                  <img src="/images/check2.png" alt="" />
                  <span>Sačuvaj zaključak</span>
                </button>
                {noteMessage && <span className="godisnji-izvestaj-note-message">{noteMessage}</span>}
              </div>
            )}
          </>
        )}

        {forPrint && (
          <div className="godisnji-izvestaj-signatures">
            <div>
              <p>Datum: {new Date().toLocaleDateString("sr-RS")}</p>
            </div>
            <div>
              <p>____________________________</p>
              <p>(potpis rukovodioca)</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const yearOptions = [];
  for (let y = new Date().getFullYear() + 1; y >= 2020; y--) yearOptions.push(y);

  return (
    <div className="godisnji-izvestaj">
      <div className="processes-header" style={{ padding: "10px", justifyContent: "space-between", alignItems: "center" }}>
        <label className="godisnji-izvestaj-year">
          Godina:
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <div style={{ marginRight: "10px" }}>
          <button className="add-btn-large" onClick={handlePrint}>
            <img alt="" src="/images/printer.png"></img>
          </button>
        </div>
      </div>

      {renderReport(false)}

      <div style={{ display: "none" }}>
        <div ref={componentRef}>{renderReport(true)}</div>
      </div>

      <Loading isLoading={loading} />
    </div>
  );
}

export default GodisnjiIzvestaj;
